import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle2, 
  Clock,
  FileText,
  MapPin,
  Calendar,
  Users,
  AlertTriangle,
  Lock,
  DollarSign,
  ArrowLeft,
  Send,
  Shield,
  Trash2,
  PenTool,
  HardHat,
  Info
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PPE display mapping
const PPE_LABELS = {
  helmet: { label: "Helmet", icon: "🪖" },
  harness: { label: "Fall Protection/Harness", icon: "🦺" },
  gloves: { label: "Gloves", icon: "🧤" },
  safety_shoes: { label: "Safety Shoes", icon: "👢" },
  hi_vis: { label: "High-Visibility Clothing", icon: "🦺" },
  glasses: { label: "Safety Glasses", icon: "🥽" },
  hearing: { label: "Hearing Protection", icon: "🎧" },
  mask: { label: "Mask/Respirator", icon: "😷" },
};

export default function AgreementDetail() {
  const { agreementId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agreement, setAgreement] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // User's allocation settings
  const [cedeTL, setCedeTL] = useState(0);
  
  // Required checkboxes
  const [checkboxes, setCheckboxes] = useState({
    terms_read: false,
    sja_read: false,
    sja_hazards_confirmed: false,
    ppe_confirmed: false,
    waiting_time_confirmed: false,
  });
  
  // Individual hazard confirmations
  const [hazardChecks, setHazardChecks] = useState({});
  
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/team-agreements/${agreementId}`);
        setAgreement(res.data);
        
        // Get project details
        if (res.data.project_id) {
          const projRes = await axios.get(`${API}/projects/${res.data.project_id}`);
          setProject(projRes.data);
          
          // Initialize hazard checkboxes
          if (projRes.data.sja_form?.hazards) {
            const initial = {};
            projRes.data.sja_form.hazards.forEach((h, i) => {
              initial[h.hazard_id || `hazard_${i}`] = false;
            });
            setHazardChecks(initial);
          }
        }
      } catch (error) {
        console.error("Error fetching agreement:", error);
        toast.error("Could not load agreement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agreementId]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#23F7DD";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [loading]);

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getSignatureData = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL("image/png");
  };

  // Get current user's member data
  const myMember = agreement?.members?.find(m => m.user_id === user?.user_id);
  const myPercentage = myMember?.current_percentage || 0;
  const myAmount = myMember?.amount || 0;
  const hasSigned = myMember?.signed || false;
  
  // Calculate amounts after TL cede
  const cedeAmount = (myAmount * cedeTL) / 100;
  const finalAmount = myAmount - cedeAmount;

  // Count signatures
  const totalMembers = agreement?.members?.length || 0;
  const signedMembers = agreement?.members?.filter(m => m.signed)?.length || 0;
  const allSigned = totalMembers > 0 && signedMembers === totalMembers;

  // Check if all hazards are confirmed
  const allHazardsConfirmed = Object.values(hazardChecks).length === 0 || 
    Object.values(hazardChecks).every(v => v === true);

  // Check if all required checkboxes are checked
  const allRequiredChecked = () => {
    const required = checkboxes.terms_read && checkboxes.sja_read;
    const hazardsOk = allHazardsConfirmed && checkboxes.sja_hazards_confirmed;
    const ppeOk = !project?.sja_form?.ppe_required?.length || checkboxes.ppe_confirmed;
    const waitingOk = !project?.waiting_time_terms?.enabled || checkboxes.waiting_time_confirmed;
    
    return required && hazardsOk && ppeOk && waitingOk && hasSignature;
  };

  const handleSign = async () => {
    if (!allRequiredChecked()) {
      toast.error("Please complete all required sections and provide your signature");
      return;
    }
    
    setSigning(true);
    try {
      const signatureData = getSignatureData();
      
      await axios.put(`${API}/team-agreements/${agreementId}/sign`, {
        signature: signatureData,
        signature_name: user?.name,
        cede_percentage: cedeTL,
        checkboxes: {
          ...checkboxes,
          hazard_confirmations: hazardChecks
        }
      });
      
      toast.success("Agreement signed successfully!");
      
      // Refresh data
      const res = await axios.get(`${API}/team-agreements/${agreementId}`);
      setAgreement(res.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  const getRiskBadge = (level) => {
    const colors = {
      green: "bg-green-500/20 text-green-400 border-green-500/50",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      red: "bg-red-500/20 text-red-400 border-red-500/50",
    };
    const labels = { green: "Low", yellow: "Medium", red: "High" };
    return (
      <Badge className={`${colors[level]} border`}>
        {labels[level]} Risk
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="Agreement Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!agreement) {
    return (
      <DashboardLayout navItems={navItems} title="Agreement Details">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Agreement Not Found</h2>
          <Button onClick={() => navigate(-1)} variant="outline" className="border-white/20 text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title="Sign Agreement">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="agreement-detail">
        {/* Back Button */}
        <Button 
          onClick={() => navigate(-1)} 
          variant="ghost" 
          className="text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Project Info Card */}
        <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">
              {project?.title || agreement.project_title || "Project Agreement"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {project?.location_city && (
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  <span>{project.location_city}</span>
                </div>
              )}
              {project?.start_date && (
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <Calendar className="w-4 h-4 text-[#23F7DD]" />
                  <span>Start: {new Date(project.start_date).toLocaleDateString()}</span>
                </div>
              )}
              {project?.end_date && (
                <div className="flex items-center gap-2 text-[#94A3B8]">
                  <Calendar className="w-4 h-4 text-[#FF5F1F]" />
                  <span>End: {new Date(project.end_date).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Users className="w-4 h-4 text-[#23F7DD]" />
                <span>{totalMembers} team members</span>
              </div>
            </div>
            
            {/* Total Budget */}
            <div className="p-4 bg-black/30 rounded-lg">
              <p className="text-sm text-[#94A3B8] mb-1">Total Team Budget</p>
              <p className="text-2xl font-bold text-[#23F7DD]">
                {(agreement.total_amount || 0).toLocaleString()} NOK
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 1: Project Terms & Conditions */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-[#23F7DD] rounded-full flex items-center justify-center text-[#050505] text-sm font-bold">1</span>
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              Project Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-black/30 rounded-lg text-sm text-[#94A3B8] space-y-3">
              <p><strong className="text-white">1. Project Scope:</strong> {project?.description || "As described in project details."}</p>
              <p><strong className="text-white">2. Location:</strong> {project?.location_address || project?.location_city || "As specified"}</p>
              <p><strong className="text-white">3. Duration:</strong> Work begins on {project?.start_date ? new Date(project.start_date).toLocaleDateString() : "TBD"} and must be completed by {project?.end_date ? new Date(project.end_date).toLocaleDateString() : "TBD"}.</p>
              <p><strong className="text-white">4. Project Type:</strong> {project?.project_type === "both" ? "Assembly & Disassembly" : project?.project_type || "Assembly"}</p>
              <p><strong className="text-white">5. Workers Required:</strong> {project?.workers_needed || totalMembers} workers</p>
              <p><strong className="text-white">6. Payment Terms:</strong> Payment will be processed within 14 days of project completion and approval.</p>
              <p><strong className="text-white">7. Safety Requirements:</strong> All workers must follow HMS regulations and wear appropriate safety equipment.</p>
              <p><strong className="text-white">8. Confidentiality:</strong> Individual payment allocations are confidential between each worker and the platform.</p>
            </div>

            {/* Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
              <Checkbox 
                id="terms_read"
                checked={checkboxes.terms_read}
                onCheckedChange={(checked) => setCheckboxes(prev => ({...prev, terms_read: checked}))}
                disabled={hasSigned}
                className="mt-1"
              />
              <Label htmlFor="terms_read" className="text-white cursor-pointer">
                I have read and understood the project terms and conditions
                <span className="text-red-400 ml-1">*</span>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Safety Job Analysis (SJA) */}
        {project?.sja_form && (
          <Card className="bg-white/5 border-red-500/30">
            <CardHeader className="bg-red-500/10">
              <CardTitle className="text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
                <Shield className="w-5 h-5 text-red-400" />
                Safety Job Analysis (SJA)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Job Description */}
              {project.sja_form.job_description && (
                <div>
                  <h4 className="text-sm text-[#94A3B8] mb-2">Job Description</h4>
                  <p className="text-white p-3 bg-white/5 rounded-lg">
                    {project.sja_form.job_description}
                  </p>
                </div>
              )}

              {/* Hazards - Each needs checkbox */}
              {project.sja_form.hazards?.length > 0 && (
                <div>
                  <h4 className="text-sm text-[#94A3B8] mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    Identified Hazards - Confirm each one
                  </h4>
                  <div className="space-y-3">
                    {project.sja_form.hazards.map((hazard, index) => {
                      const hazardId = hazard.hazard_id || `hazard_${index}`;
                      return (
                        <div 
                          key={hazardId} 
                          className={`p-4 rounded-lg border transition-all ${
                            hazardChecks[hazardId] 
                              ? "bg-green-500/10 border-green-500/30" 
                              : "bg-white/5 border-red-500/30"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={hazardId}
                              checked={hazardChecks[hazardId] || false}
                              onCheckedChange={(checked) => setHazardChecks(prev => ({...prev, [hazardId]: checked}))}
                              disabled={hasSigned}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <Label htmlFor={hazardId} className="text-white font-medium cursor-pointer">
                                  {hazard.description}
                                </Label>
                                {getRiskBadge(hazard.risk_level)}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                                <div>
                                  <span className="text-[#64748B]">Probability:</span>
                                  <span className="text-white ml-2 capitalize">{hazard.probability}</span>
                                </div>
                                <div>
                                  <span className="text-[#64748B]">Consequence:</span>
                                  <span className="text-white ml-2 capitalize">{hazard.consequence}</span>
                                </div>
                              </div>
                              
                              {hazard.preventive_measures && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded">
                                  <span className="text-green-400 text-sm font-medium">Preventive Measures:</span>
                                  <p className="text-green-300 text-sm mt-1">{hazard.preventive_measures}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Emergency Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div>
                  <span className="text-[#64748B] text-sm">Emergency Contact</span>
                  <p className="text-white">{project.sja_form.emergency_contact || "113"}</p>
                </div>
                <div>
                  <span className="text-[#64748B] text-sm">Nearest Hospital</span>
                  <p className="text-white">{project.sja_form.nearest_hospital || "Contact supervisor"}</p>
                </div>
                <div>
                  <span className="text-[#64748B] text-sm">First Aid Location</span>
                  <p className="text-white">{project.sja_form.first_aid_location || "On site"}</p>
                </div>
              </div>

              {/* SJA Confirmation Checkboxes */}
              <div className="space-y-3 pt-4 border-t border-white/10">
                <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                  <Checkbox
                    id="sja_read"
                    checked={checkboxes.sja_read}
                    onCheckedChange={(checked) => setCheckboxes(prev => ({...prev, sja_read: checked}))}
                    disabled={hasSigned}
                    className="mt-1"
                  />
                  <Label htmlFor="sja_read" className="text-white cursor-pointer">
                    I have read the complete Safety Job Analysis (SJA)
                    <span className="text-red-400 ml-1">*</span>
                  </Label>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                  <Checkbox
                    id="sja_hazards_confirmed"
                    checked={checkboxes.sja_hazards_confirmed}
                    onCheckedChange={(checked) => setCheckboxes(prev => ({...prev, sja_hazards_confirmed: checked}))}
                    disabled={hasSigned || !allHazardsConfirmed}
                    className="mt-1"
                  />
                  <Label htmlFor="sja_hazards_confirmed" className="text-white cursor-pointer">
                    I understand ALL identified hazards and their preventive measures
                    <span className="text-red-400 ml-1">*</span>
                    {!allHazardsConfirmed && (
                      <span className="block text-yellow-400 text-sm mt-1">
                        Please confirm each hazard above first
                      </span>
                    )}
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 3: Required PPE */}
        {project?.sja_form?.ppe_required?.length > 0 && (
          <Card className="bg-white/5 border-yellow-500/30">
            <CardHeader className="bg-yellow-500/10">
              <CardTitle className="text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-black text-sm font-bold">3</span>
                <HardHat className="w-5 h-5 text-yellow-400" />
                Required Personal Protective Equipment (PPE)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {project.sja_form.ppe_required.map((ppeId) => {
                  const ppe = PPE_LABELS[ppeId] || { label: ppeId, icon: "🛡️" };
                  return (
                    <div
                      key={ppeId}
                      className="p-3 rounded-lg bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 flex items-center gap-2"
                    >
                      <span className="text-xl">{ppe.icon}</span>
                      <span className="text-sm">{ppe.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <Checkbox
                  id="ppe_confirmed"
                  checked={checkboxes.ppe_confirmed}
                  onCheckedChange={(checked) => setCheckboxes(prev => ({...prev, ppe_confirmed: checked}))}
                  disabled={hasSigned}
                  className="mt-1"
                />
                <Label htmlFor="ppe_confirmed" className="text-white cursor-pointer">
                  I confirm I will wear ALL required PPE at all times during this project
                  <span className="text-red-400 ml-1">*</span>
                </Label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 4: Waiting Time Terms */}
        {project?.waiting_time_terms?.enabled && (
          <Card className="bg-white/5 border-orange-500/30">
            <CardHeader className="bg-orange-500/10">
              <CardTitle className="text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {project?.sja_form?.ppe_required?.length > 0 ? "4" : "3"}
                </span>
                <Clock className="w-5 h-5 text-orange-400" />
                Waiting Time Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-[#64748B] text-sm">Maximum Waiting Hours</span>
                    <p className="text-white text-xl font-bold">
                      {project.waiting_time_terms.max_hours_per_person} hours
                    </p>
                  </div>
                  <div>
                    <span className="text-[#64748B] text-sm">Hourly Rate</span>
                    <p className="text-[#23F7DD] text-xl font-bold">
                      {project.waiting_time_terms.hourly_rate} NOK/hour
                    </p>
                  </div>
                </div>
                
                {project.waiting_time_terms.reasons && (
                  <div>
                    <span className="text-[#64748B] text-sm">Reasons for Potential Waiting</span>
                    <p className="text-white mt-1">{project.waiting_time_terms.reasons}</p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-400 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> The waiting time compensation is <strong>already included</strong> in the total project price. 
                    If the project is completed earlier than expected, this amount will <strong>not be deducted</strong> from your payment.
                  </span>
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <Checkbox
                  id="waiting_time_confirmed"
                  checked={checkboxes.waiting_time_confirmed}
                  onCheckedChange={(checked) => setCheckboxes(prev => ({...prev, waiting_time_confirmed: checked}))}
                  disabled={hasSigned}
                  className="mt-1"
                />
                <Label htmlFor="waiting_time_confirmed" className="text-white cursor-pointer">
                  I accept the waiting time terms and conditions
                  <span className="text-red-400 ml-1">*</span>
                </Label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Signatures Status */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#23F7DD]" />
              Team Signatures ({signedMembers}/{totalMembers})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {agreement.members?.map((member, idx) => {
                const isMe = member.user_id === user?.user_id;
                
                return (
                  <div 
                    key={member.user_id || idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isMe ? "bg-[#23F7DD]/10 border border-[#23F7DD]/30" : "bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        member.signed ? "bg-green-500/20" : "bg-yellow-500/20"
                      }`}>
                        {member.signed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {member.user_name || member.name || "Team Member"} {isMe && "(You)"}
                        </p>
                        <p className="text-xs text-[#94A3B8]">
                          {member.role === "team_leader" ? "Team Leader" : "Worker"}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {isMe ? (
                        <div className="text-right">
                          <p className="text-[#23F7DD] font-bold">{myPercentage}%</p>
                          <p className="text-xs text-[#94A3B8]">{myAmount.toLocaleString()} NOK</p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-[#94A3B8]">
                          <Lock className="w-4 h-4" />
                          <span className="text-xs">Confidential</span>
                        </div>
                      )}
                      
                      <span className={`px-2 py-1 text-xs rounded ${
                        member.signed 
                          ? "bg-green-500/20 text-green-400" 
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        {member.signed ? "Signed" : "Pending"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {allSigned && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">All members have signed! The project is ready to begin.</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Allocation & Signing Section */}
        {!hasSigned ? (
          <Card className="bg-white/5 border-[#23F7DD]/30">
            <CardHeader className="bg-[#23F7DD]/10">
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                Your Allocation & Signature
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Your Allocation */}
              <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#94A3B8]">Your Base Allocation</span>
                  <span className="text-xl font-bold text-[#23F7DD]">{myPercentage}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#94A3B8]">Amount</span>
                  <span className="text-2xl font-bold text-white">{myAmount.toLocaleString()} NOK</span>
                </div>
              </div>

              {/* Cede to Team Leader - More visible */}
              <div className="p-4 bg-[#4F46E5]/10 border border-[#4F46E5]/30 rounded-lg space-y-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-[#4F46E5] flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-medium">Bonus for Team Leader (Optional)</h4>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      You can optionally give up to 3% of your allocation to the Team Leader as a bonus for organizing the team.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-3 bg-black/30 rounded-lg">
                  <span className="text-white font-medium w-8">0%</span>
                  <Slider
                    value={[cedeTL]}
                    onValueChange={(v) => setCedeTL(v[0])}
                    max={3}
                    min={0}
                    step={0.5}
                    className="flex-1"
                  />
                  <span className="text-white font-bold w-12 text-right">{cedeTL}%</span>
                </div>
                
                {cedeTL > 0 && (
                  <div className="p-3 bg-[#4F46E5]/20 rounded-lg">
                    <p className="text-white">
                      You give <span className="text-[#4F46E5] font-bold">{cedeAmount.toLocaleString()} NOK</span> to Team Leader
                    </p>
                    <p className="text-[#23F7DD] font-bold mt-1">
                      Your final payment: {finalAmount.toLocaleString()} NOK
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-[#94A3B8] flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  This is confidential - only you and the platform admin can see this.
                </p>
              </div>

              {/* Signature Canvas */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white flex items-center gap-2">
                    <PenTool className="w-4 h-4 text-[#23F7DD]" />
                    Draw Your Signature
                    <span className="text-red-400">*</span>
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="text-[#94A3B8] hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
                
                <div className="border-2 border-dashed border-white/20 rounded-lg bg-black/50 overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={150}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="w-full h-[150px] cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                
                {!hasSignature && (
                  <p className="text-sm text-yellow-400 flex items-center gap-1">
                    <Info className="w-4 h-4" />
                    Please draw your signature above using mouse or finger
                  </p>
                )}
                
                {hasSignature && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-400 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Signature provided for: <strong>{user?.name}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Sign Button */}
              <Button
                onClick={handleSign}
                disabled={signing || !allRequiredChecked()}
                className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg disabled:opacity-50"
              >
                {signing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                    Signing...
                  </span>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Sign Agreement
                  </>
                )}
              </Button>

              {!allRequiredChecked() && (
                <p className="text-center text-yellow-400 text-sm">
                  <Info className="w-4 h-4 inline mr-1" />
                  Please complete all required sections above and provide your signature
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Already Signed */
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-400">Agreement Signed</h3>
                  <p className="text-[#94A3B8]">
                    You signed this agreement on {new Date(myMember?.signed_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
